/**
 * PhaseMilestoneStream — the main stream: the current PHASE and the current
 * MILESTONE, readable with nothing expanded (SC-9, AC-1).
 *
 * Visual contract: `features/1-product-req-docs/emuw-v1-5-target-experience-0824/artifacts/emuw-drawers-extracted/RunningDrawer.dc.html`
 * (EMUW Drawers, 440×920). Collapsed, the block shows one phase and one
 * sub-step. "All phases" reveals the rail; each phase's sub-steps stay
 * collapsed to the current one until that phase's right-hand chevron opens.
 *
 * Shared by ora's WorkflowDetailDrawer and the add-in's InstanceDetailDrawer.
 * There is no host-local copy: a fork in the add-in fails
 * every Type-B probe's `require_shared_source` (design Constraint 20).
 *
 * The 51 underlying STEPS never appear here. They live only in the detailed
 * chronological log behind the more-information entry point (AD-PLAN-3), and
 * the must_pass probe fails if a step id or step display name reaches this
 * component.
 *
 * No fetch, no timer, no `Office.*`.
 */

import { useState } from "react";
import type { PhaseRow, PhaseRowState, PhaseStreamModel } from "../../phaseModel";

/**
 * Every state carries a TEXT label. Colour is never the only channel
 * (DoD Accessibility) — the label is what a screen reader announces and what
 * a colour-blind reader reads.
 */
const STATE_LABEL: Record<PhaseRowState, string> = {
  completed: "Done",
  running: "In progress",
  hitl_waiting: "Waiting on you",
  failed: "Failed",
  not_reached: "Not started",
  unknown: "Not reported",
};

const PHASE_COUNT_WORDS = [
  "ZERO",
  "ONE",
  "TWO",
  "THREE",
  "FOUR",
  "FIVE",
  "SIX",
  "SEVEN",
  "EIGHT",
  "NINE",
  "TEN",
];

/** Copy fixed by the PRD's honest-states rule. */
export const PRE_MODEL_NOTICE = "This run started before stage reporting";
export const UNDECLARED_NOTICE =
  "This workflow does not report stages or milestones";
/** The small caps line above the notice. "SIGNING IN" for the two states the
 *  run handles by itself; "ACTION NEEDED" only when the reader is the blocker,
 *  so the phrase keeps its meaning when it appears. */
export const SIGN_IN_EYEBROW = {
  first: "SIGNING IN",
  again: "SIGNING IN",
  awaiting_mfa: "ACTION NEEDED",
} as const;

/** Sign-in copy. The line this replaced — "Epic session: shared SP — lease
 *  held" — described OUR plumbing (a service-principal account held under a
 *  lease) to a reader who wants to know what is happening to THEIR request.
 *  "Epic" stays: it is the system they use daily. "SP" and "lease" go.
 *
 *  Three states rather than one, because the only distinction a Servicer acts
 *  on is whether they are waiting or being waited FOR. Honest-states rule: none
 *  of these implies progress that is not happening. */
export const SIGN_IN_NOTICE = {
  first: "Signing in to Epic",
  again: "Signing back in to Epic",
  awaiting_mfa: "Waiting for you to approve the sign-in",
} as const;

export const NO_LEGACY_STEP_NOTICE = "Current step not reported";

export interface PhaseMilestoneStreamProps {
  model: PhaseStreamModel;
}

function phasesDoneLabel(n: number): string {
  const word = PHASE_COUNT_WORDS[n] ?? String(n);
  return `ALL ${word} STAGES DONE`;
}

function currentPhaseIndex(rows: PhaseRow[]): number {
  return rows.findIndex((r) => r.current);
}

function currentMilestoneOf(row: PhaseRow | null) {
  if (!row) return null;
  return (
    row.milestones.find((m) => m.current) ??
    (row.state === "completed"
      ? row.milestones[row.milestones.length - 1]
      : row.milestones[0]) ??
    null
  );
}

function visibleMilestones(row: PhaseRow, subsOpen: boolean) {
  if (subsOpen) return row.milestones;
  const one = currentMilestoneOf(row);
  return one ? [one] : [];
}

/** Coloured mark + accessible text. Colour is never the only channel. */
function StageMark({ state }: { state: PhaseRowState }) {
  if (state === "completed") {
    return (
      <span
        className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary-success-700"
        data-testid="phase-stream-mark-completed"
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m5 13 4 4L19 7"
            stroke="#fff"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="sr-only">{STATE_LABEL[state]}</span>
      </span>
    );
  }
  if (state === "running" || state === "hitl_waiting") {
    return (
      <span
        className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${
          state === "running"
            ? "border-sunrise bg-[#FFFDF0]"
            : "border-primary-warning-500 bg-primary-warning-50"
        }`}
        data-testid={`phase-stream-mark-${state}`}
      >
        <span
          className={`h-1.5 w-1.5 rounded-full ${
            state === "running" ? "bg-primary-gray-light-900" : "bg-primary-warning-700"
          }`}
          aria-hidden="true"
        />
        <span className="sr-only">{STATE_LABEL[state]}</span>
      </span>
    );
  }
  if (state === "failed") {
    return (
      <span
        className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary-error-500 text-2xs font-bold text-white"
        data-testid="phase-stream-mark-failed"
      >
        !
        <span className="sr-only">{STATE_LABEL[state]}</span>
      </span>
    );
  }
  return (
    <span
      className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-primary-gray-light-300 bg-base-white"
      data-testid="phase-stream-mark-pending"
    >
      <span className="sr-only">{STATE_LABEL[state]}</span>
    </span>
  );
}

function SubDot({ state }: { state: PhaseRowState }) {
  const tone =
    state === "completed"
      ? "border-primary-success-600 bg-primary-success-200"
      : state === "running" || state === "hitl_waiting"
        ? "border-transparent bg-[#F37B24]"
        : state === "failed"
          ? "border-primary-error-500 bg-primary-error-200"
          : "border-primary-gray-light-300 bg-base-white";
  return (
    <span
      className={`h-2.5 w-2.5 flex-shrink-0 rounded-full border ${tone}`}
      aria-hidden="true"
    />
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <span
      className="text-primary-gray-light-400 text-xs leading-none transition-transform duration-150"
      aria-hidden="true"
      style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
    >
      ›
    </span>
  );
}

function MilestoneRows({
  milestones,
}: {
  milestones: PhaseStreamModel["rows"][number]["milestones"];
}) {
  if (milestones.length === 0) return null;
  return (
    <ul className="mt-1 flex flex-col gap-1">
      {milestones.map((m) => (
        <li
          key={m.id}
          className="flex items-center gap-2"
          data-testid="phase-stream-milestone-row"
          data-current={m.current ? "true" : "false"}
        >
          <SubDot state={m.state} />
          <span
            className={
              m.current
                ? "text-xs font-semibold text-primary-gray-light-900"
                : m.state === "completed"
                  ? "text-xs text-primary-gray-light-700"
                  : "text-xs text-primary-gray-light-400"
            }
          >
            {m.label}
            {m.current ? " (current)" : ""}
          </span>
          <span className="sr-only">{STATE_LABEL[m.state]}</span>
        </li>
      ))}
    </ul>
  );
}

function CurrentBlock({
  model,
  currentRow,
  currentMilestone,
}: {
  model: PhaseStreamModel;
  currentRow: PhaseRow | null;
  currentMilestone: PhaseRow["milestones"][number] | null;
}) {
  const isModel = model.source === "model";
  return (
    <div data-testid="phase-stream-current">
      {!isModel ? (
        <>
          <p className="text-xs text-primary-gray-light-700">
            {model.source === "pre_model" ? PRE_MODEL_NOTICE : UNDECLARED_NOTICE}
          </p>
          <p
            className="text-sm font-semibold text-primary-gray-light-800"
            data-testid="phase-stream-legacy-step"
          >
            {model.legacyStepLabel ?? NO_LEGACY_STEP_NOTICE}
          </p>
        </>
      ) : model.stepExempt ? (
        <p
          className={
            model.signInState === "awaiting_mfa"
              ? "text-sm font-semibold text-primary-gray-light-900"
              : "text-sm font-semibold text-primary-gray-light-800"
          }
          data-testid="phase-stream-login-sequence"
          data-sign-in-state={model.signInState ?? "first"}
        >
          {SIGN_IN_NOTICE[model.signInState ?? "first"]}
        </p>
      ) : (
        <div className="flex items-start gap-3">
          {currentRow ? <StageMark state={currentRow.state} /> : null}
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <p
              className="text-sm font-semibold text-primary-gray-light-900"
              data-testid="phase-stream-phase-label"
            >
              {model.phaseLabel ?? "Stage not reported"}
            </p>
            {currentMilestone ? (
              <div className="flex items-center gap-1.5">
                <SubDot state={currentMilestone.state} />
                <p
                  className="text-xs text-primary-gray-light-700"
                  data-testid="phase-stream-milestone-label"
                >
                  {model.milestoneLabel ?? currentMilestone.label}
                </p>
              </div>
            ) : (
              <p
                className="text-xs text-primary-gray-light-700"
                data-testid="phase-stream-milestone-label"
              >
                {model.milestoneLabel ?? "Milestone not reported"}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function PhaseMilestoneStream({ model }: PhaseMilestoneStreamProps) {
  // Component-local, per-open. Deliberately NOT DrawerSection/persistKey.
  const [allOpen, setAllOpen] = useState(false);
  const [subOpen, setSubOpen] = useState<Record<string, boolean>>({});

  const isModel = model.source === "model";
  const currentRow = model.rows.find((r) => r.current) ?? null;
  const currentMilestone = currentMilestoneOf(currentRow);
  const currentIdx = currentPhaseIndex(model.rows);
  const allDone =
    isModel &&
    model.rows.length > 0 &&
    model.rows.every((r) => r.state === "completed");
  const progressLabel = model.stepExempt
    ? SIGN_IN_EYEBROW[model.signInState ?? "first"]
    : allDone
      ? phasesDoneLabel(model.rows.length)
      : currentIdx >= 0
        ? `STAGE ${currentIdx + 1} OF ${model.rows.length}`
        : null;
  const showPhaseChrome = isModel && model.rows.length > 0;

  return (
    <div
      className="mb-2 flex flex-col gap-3 rounded-lg border border-primary-gray-light-200 bg-base-white p-3.5"
      data-testid="phase-milestone-stream"
      data-source={model.source}
    >
      {showPhaseChrome && (
        <button
          type="button"
          aria-expanded={allOpen}
          onClick={() => setAllOpen((v) => !v)}
          className="flex w-full items-center gap-2 text-left"
          data-testid="phase-stream-all-phases"
        >
          <span
            className="flex-1 text-2xs font-semibold uppercase tracking-wide text-primary-gray-light-500"
            data-testid="phase-stream-counter"
          >
            {progressLabel}
          </span>
          <span className="text-xs text-primary-gray-light-400">
            {allOpen ? "Hide all" : "All stages"}
          </span>
          <Chevron open={allOpen} />
        </button>
      )}

      {/* Collapsed current block stays in the DOM when the rail opens so
          expanding never moves it; `hidden` matches the artboard (rail
          replaces the one-phase view on screen). */}
      <div hidden={showPhaseChrome && allOpen}>
        <CurrentBlock
          model={model}
          currentRow={currentRow}
          currentMilestone={currentMilestone}
        />
      </div>

      {showPhaseChrome && allOpen && (
        <ul className="flex flex-col" data-testid="phase-stream-rail">
          {model.rows.map((row, i) => {
            const open = subOpen[row.id] === true;
            // A finished stage is never "(current)" — e.g. Pending Email Send,
            // where the stage is done and the current line inside it is the
            // AM's "Send the reply" (story 28633).
            const name =
              row.current && row.state !== "completed" ? `${row.label} (current)` : row.label;
            const shown = visibleMilestones(row, open);
            const last = i === model.rows.length - 1;
            return (
              <li key={row.id} data-testid="phase-stream-phase-row">
                <div className="flex items-start gap-3">
                  <div className="flex flex-shrink-0 flex-col items-center self-stretch">
                    <StageMark state={row.state} />
                    {!last && (
                      <span
                        className={`mt-0.5 w-0.5 flex-1 min-h-[14px] ${
                          row.state === "completed"
                            ? "bg-primary-success-200"
                            : "bg-primary-gray-light-200"
                        }`}
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <div className={`min-w-0 flex-1 ${last ? "pb-0" : "pb-3.5"}`}>
                    <button
                      type="button"
                      aria-expanded={open}
                      onClick={() =>
                        setSubOpen((prev) => ({ ...prev, [row.id]: !open }))
                      }
                      className="flex w-full items-center gap-2 text-left"
                      data-testid="phase-stream-phase-toggle"
                    >
                      <span
                        className={
                          row.current
                            ? "text-xs font-semibold text-primary-gray-light-900"
                            : row.state === "not_reached" || row.state === "unknown"
                              ? "text-xs text-primary-gray-light-400"
                              : "text-xs text-primary-gray-light-700"
                        }
                      >
                        {name}
                      </span>
                      <span className="flex-1" />
                      <span className="flex-shrink-0 text-2xs text-primary-gray-light-400">
                        {open ? "hide" : `${row.milestones.length} steps`}
                      </span>
                      <Chevron open={open} />
                    </button>
                    <MilestoneRows milestones={shown} />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default PhaseMilestoneStream;
